import { Canvas } from "@react-three/fiber";
import { OrbitControls, Environment } from "@react-three/drei";
import type { Model3DGeometry } from "../../services/client-models.service";

interface ClientViewport3DProps {
  geometry: Model3DGeometry;
  wireframe?: boolean;
  height?: number;
  onReady?: () => void;
}

export function ClientViewport3D({
  geometry,
  wireframe = false,
  height = 500,
  onReady,
}: ClientViewport3DProps) {
  const { dimensions, floor, walls, roof, windows, doors, materials } = geometry;
  const targetY = dimensions.totalHeight / 2;

  return (
    <div
      className="relative rounded-xl overflow-hidden bg-gradient-to-b from-slate-100 to-slate-200"
      style={{ height, width: "100%" }}
    >
      <Canvas
        camera={{ position: [dimensions.width * 1.4, dimensions.totalHeight * 1.6, dimensions.depth * 1.6], fov: 45 }}
        shadows
        onCreated={({ gl }) => {
          gl.setClearColor("#e2e8f0");
          if (onReady) onReady();
        }}
      >
        <ambientLight intensity={0.55} color="#ffffff" />
        <directionalLight
          position={[15, 20, 10]}
          intensity={1.1}
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
        />
        <directionalLight position={[-10, 8, -10]} intensity={0.35} color="#c7d2fe" />

        {/* Piso (terreno) */}
        <mesh
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, -0.11, 0]}
          receiveShadow
        >
          <planeGeometry args={[dimensions.width * 3, dimensions.depth * 3]} />
          <meshStandardMaterial color="#cbd5e1" roughness={0.9} metalness={0} />
        </mesh>

        {/* Piso del edificio */}
        <mesh position={[0, floor.y, 0]} castShadow receiveShadow>
          <boxGeometry args={[floor.width, 0.1, floor.depth]} />
          <meshStandardMaterial
            color={materials.floor}
            roughness={0.7}
            metalness={0.05}
            wireframe={wireframe}
          />
        </mesh>

        {/* Paredes */}
        {walls.map((wall) => (
          <mesh
            key={wall.id}
            position={[wall.x, wall.y, wall.z]}
            castShadow
            receiveShadow
          >
            <boxGeometry args={[wall.width, wall.height, wall.depth]} />
            <meshStandardMaterial
              color={materials.wall}
              roughness={0.75}
              metalness={0.05}
              wireframe={wireframe}
            />
          </mesh>
        ))}

        {/* Techo */}
        <mesh position={[0, roof.y, 0]} castShadow>
          <boxGeometry args={[roof.width, roof.height, roof.depth]} />
          <meshStandardMaterial
            color={materials.roof}
            roughness={0.75}
            metalness={0.1}
            wireframe={wireframe}
          />
        </mesh>

        {/* Ventanas */}
        {windows.map((win) => (
          <mesh key={win.id} position={[win.x, win.y, win.z]}>
            <boxGeometry args={[win.width, win.height, 0.08]} />
            <meshPhysicalMaterial
              color={materials.window}
              roughness={0.1}
              metalness={0}
              transparent
              opacity={0.55}
            />
          </mesh>
        ))}

        {/* Puertas */}
        {doors.map((door) => (
          <mesh key={door.id} position={[door.x, door.y, door.z]}>
            <boxGeometry args={[door.width, door.height, 0.08]} />
            <meshStandardMaterial color={materials.door} roughness={0.85} />
          </mesh>
        ))}

        <OrbitControls
          enableDamping
          dampingFactor={0.08}
          minDistance={3}
          maxDistance={Math.max(dimensions.width, dimensions.depth) * 3}
          maxPolarAngle={Math.PI / 2.05}
          target={[0, targetY, 0]}
        />

        <Environment preset="city" />
      </Canvas>

      {/* Leyenda */}
      <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur text-xs px-3 py-2 rounded-lg shadow-sm">
        <div className="font-semibold text-slate-700 mb-1">Medidas</div>
        <div className="text-slate-500">
          {dimensions.width}m × {dimensions.depth}m × {dimensions.totalHeight}m
        </div>
        <div className="text-slate-400 mt-0.5">{dimensions.floors} {dimensions.floors === 1 ? "piso" : "pisos"}</div>
      </div>
    </div>
  );
}